import {
    type RefObject,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react"
import {
    SupportedLanguage,
    getStoredLanguage,
    setStoredLanguage
} from "@/utils/languageStorage"
import {
    downloadLanguageModel,
    isModelDownloaded,
    isTranslationAvailable,
    translateText
} from "@/utils/translationService"
import { getOverride } from "@/utils/translationOverrides"

interface LanguageContextType {
    currentLanguage: SupportedLanguage
    setLanguage: (language: SupportedLanguage) => Promise<void>
    translate: (text: string) => string
    isDownloading: boolean
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = async () => {}

const LanguageContext = createContext<LanguageContextType>({
    currentLanguage: "ja",
    setLanguage: noop,
    translate: text => text,
    isDownloading: false
})

export const useLanguage = () => useContext(LanguageContext)

export const LanguageProvider = ({ children }: PropsWithChildren) => {
    const [currentLanguage, setCurrentLanguage] =
        useState<SupportedLanguage>("ja")
    const [isDownloading, setIsDownloading] = useState(false)
    const [translations, setTranslations] = useState<Map<string, string>>(
        new Map()
    )
    const pendingRef = useRef<Set<string>>(new Set())
    const batchRef = useRef<Map<string, string>>(new Map())
    const rafRef = useRef<number | null>(null)

    const flushBatch = useCallback(() => {
        rafRef.current = null
        if (batchRef.current.size === 0) return
        const batch = batchRef.current
        batchRef.current = new Map()
        setTranslations(prev => {
            const next = new Map(prev)
            batch.forEach((value, key) => next.set(key, value))
            return next
        })
    }, [])

    const translate = useCallback(
        (text: string): string => {
            if (currentLanguage === "ja") return text
            const override = getOverride(currentLanguage, text)
            if (override !== undefined) return override
            return translations.get(text) ?? text
        },
        [currentLanguage, translations]
    )

    const requestTranslation = useCallback(
        (text: string) => {
            if (currentLanguage === "ja") return
            if (getOverride(currentLanguage, text) !== undefined) return
            if (translations.has(text)) return
            if (pendingRef.current.has(text)) return

            pendingRef.current.add(text)
            translateText(text, currentLanguage).then(translated => {
                pendingRef.current.delete(text)
                if (translated !== text) {
                    batchRef.current.set(text, translated)
                    if (rafRef.current === null) {
                        rafRef.current = requestAnimationFrame(flushBatch)
                    }
                }
            })
        },
        [currentLanguage, translations]
    )

    // Expose requestTranslation via a ref so useT can access it without subscribing to re-renders.
    // render中に t() が requestTranslationRef.current を呼ぶため、ref の更新は同期的に行う必要がある。
    // useEffect に移すと言語切替直後の1レンダーで stale な関数（currentLanguage="ja"）が呼ばれ、
    // early return されて翻訳リクエストが発火しない不具合になる。
    const requestTranslationRef = useRef(requestTranslation)
    // eslint-disable-next-line react-hooks/refs -- 上記コメント参照: render中の同期更新が必須
    requestTranslationRef.current = requestTranslation

    const setLanguage = useCallback(
        async (language: SupportedLanguage) => {
            if (language === currentLanguage) return

            if (language !== "ja" && isTranslationAvailable()) {
                const downloaded = await isModelDownloaded(language)
                if (!downloaded) {
                    setIsDownloading(true)
                    try {
                        await downloadLanguageModel(language)
                    } finally {
                        setIsDownloading(false)
                    }
                }
            }

            await setStoredLanguage(language)
            setCurrentLanguage(language)
            setTranslations(new Map())
            pendingRef.current.clear()
            batchRef.current.clear()
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
        },
        [currentLanguage]
    )

    // On mount, load stored language (no auto-download)
    useEffect(() => {
        const init = async () => {
            const stored = await getStoredLanguage()
            if (stored !== "ja") {
                if (isTranslationAvailable()) {
                    const downloaded = await isModelDownloaded(stored)
                    if (!downloaded) {
                        // モデル未ダウンロードなら日本語にフォールバック
                        await setStoredLanguage("ja")
                        return
                    }
                }
                setCurrentLanguage(stored)
            }
        }
        init()
    }, [])

    return (
        <LanguageContext.Provider
            value={{ currentLanguage, setLanguage, translate, isDownloading }}
        >
            <RequestTranslationContext.Provider value={requestTranslationRef}>
                {children}
            </RequestTranslationContext.Provider>
        </LanguageContext.Provider>
    )
}

const noopFn = (_text: string) => {} // eslint-disable-line @typescript-eslint/no-empty-function

// Separate context for requestTranslation to avoid unnecessary re-renders
export const RequestTranslationContext = createContext<
    RefObject<(text: string) => void>
>({ current: noopFn })
